import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router';
import { Button, Flex, Input, Modal, Spin, Typography, message } from 'antd';
import { isAxiosError } from 'axios';
import { ArrowRight, ChevronLeft, Pause, PlayCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';

import { activateAiAgent, getGetAiAgentQueryKey, pauseAiAgent } from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import { AgentLayout } from '../../../../modules/ai-agent/components/agent-layout';
import { useGetAiAgentQuery, useUpdateAiAgentMutation } from '../../../../modules/ai-agent/hooks';
import { AiAssistantThread } from '../../../../modules/ai-next/components';
import { useAiAssistant } from '../../../../modules/ai-next/providers';
import { AdaptiveSegmented } from '../../../../ui/components/adaptive-segmented';

type RightTab = 'preview' | 'activity' | 'webhook' | 'deliveries';

const tabFromPathname = (pathname: string): RightTab => {
  if (pathname.endsWith('/activity')) return 'activity';
  if (pathname.endsWith('/webhook')) return 'webhook';
  if (pathname.endsWith('/deliveries')) return 'deliveries';
  return 'preview';
};

const AgentDetailLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const { agentId } = Route.useParams();
  const organizationId = selectedOrganization.id;

  const queryClient = useQueryClient();

  const {
    data: agentResponse,
    isLoading: isAgentLoading,
    error: agentError,
  } = useGetAiAgentQuery(organizationId, agentId);

  // Agent was deleted, or the active organization was switched to one that
  // doesn't own this agent: GET returns 404. Redirect back to the agents list
  // instead of leaving the page stuck on a cross-org id.
  useEffect(() => {
    if (isAxiosError(agentError) && agentError.response?.status === 404) {
      void navigate({ to: '/agents' });
    }
  }, [agentError, navigate]);
  const updateMutation = useUpdateAiAgentMutation(organizationId);
  const agentData = agentResponse?.data;
  const { can } = usePermissions();
  const canViewAgentChat = Boolean(agentData?.builtInAssistantThread) && can('agents', 'view');
  const canEditAgent = can('agents', 'edit');

  const { uiContext } = useAiAssistant();

  useEffect(() => {
    if (agentData) {
      uiContext.setScopedFlow({
        agentBuilder: {
          agentId: agentData.id,
          name: agentData.name,
          goal: agentData.goal,
          tools: agentData.tools,
          triggers: agentData.triggers,
          webhook: {
            url: agentData.webhookUrl,
            enabled: agentData.webhookEnabled,
            mapping: agentData.webhookMapper && {
              targets: agentData.webhookMapper.targets,
              fields: agentData.webhookMapper.fields.map((field) => ({
                sourceLabel: field.sourceLabel,
                targets: [field.target, ...(field.alsoTargets ?? [])]
                  .filter(Boolean)
                  .map((target) => `${target!.recordType}.${target!.field ?? 'customField'}`),
              })),
              constants: (agentData.webhookMapper.constants ?? []).map((constant) => ({
                target: `${constant.target.recordType}.${constant.target.field ?? 'customField'}`,
                value:
                  constant.value.kind === 'fixed'
                    ? constant.value.value
                    : `conditional (${constant.value.when.length} rules)`,
              })),
            },
          },
          schedule: agentData.schedule,
          constraints: agentData.constraints,
          steps: agentData.steps,
        },
      });
    }

    return () => {
      uiContext.clearScopedFlow();
    };
  }, [agentData, uiContext]);

  // ── Agent name editing ──
  const [agentName, setAgentName] = useState('');
  const nameInitialized = useRef(false);

  useEffect(() => {
    if (agentData && !nameInitialized.current) {
      setAgentName(agentData.name);
      nameInitialized.current = true;
    }
  }, [agentData]);

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAgentName(e.target.value);
  }, []);

  const handleNameBlur = useCallback(() => {
    if (agentData && agentName.trim() && agentName !== agentData.name) {
      updateMutation.mutate({ aiAgentId: agentId, data: { name: agentName.trim() } });
    }
  }, [agentData, agentName, agentId, updateMutation]);

  // ── Tab / route navigation ──
  const currentTab = tabFromPathname(location.pathname);
  const isDraft = agentData?.state === 'draft';

  const tabOptions = [
    { value: 'preview', label: 'Agent' },
    { value: 'activity', label: 'Activity', disabled: isDraft },
    { value: 'webhook', label: 'Webhook', disabled: isDraft },
    { value: 'deliveries', label: 'Deliveries', disabled: isDraft },
  ];

  useEffect(() => {
    if (isDraft && currentTab !== 'preview') {
      void navigate({
        to: '/agents/$agentId',
        params: { agentId },
        replace: true,
      });
    }
  }, [isDraft, currentTab, navigate, agentId]);

  const handleTabChange = useCallback(
    (value: string) => {
      if (isDraft && value !== 'preview') {
        return;
      }
      const tab = value as RightTab;
      if (tab === 'preview') {
        void navigate({ to: '/agents/$agentId', params: { agentId } });
      } else {
        void navigate({
          to: `/agents/$agentId/${tab}` as '/agents/$agentId/activity',
          params: { agentId },
        });
      }
    },
    [navigate, agentId, isDraft],
  );

  // ── Launch / Pause / Resume ──
  const [isLaunching, setIsLaunching] = useState(false);

  const invalidateAgent = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: getGetAiAgentQueryKey(organizationId, agentId),
    });
    void queryClient.invalidateQueries({ queryKey: ['ai-agents', organizationId] });
  }, [queryClient, organizationId, agentId]);

  const handleLaunch = useCallback(async () => {
    setIsLaunching(true);
    try {
      await activateAiAgent(organizationId, agentId);
      invalidateAgent();
      message.success('Agent launched');
    } catch {
      message.error('Failed to launch agent');
    } finally {
      setIsLaunching(false);
    }
  }, [organizationId, agentId, invalidateAgent]);

  const handlePause = useCallback(() => {
    Modal.confirm({
      title: 'Pause agent?',
      content:
        'Any runs and scheduled emails or SMS this agent has lined up will be cancelled. Reactivating it will not bring them back.',
      okText: 'Pause',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        setIsLaunching(true);
        try {
          await pauseAiAgent(organizationId, agentId);
          invalidateAgent();
          message.success('Agent paused');
        } catch {
          message.error('Failed to pause agent');
        } finally {
          setIsLaunching(false);
        }
      },
    });
  }, [organizationId, agentId, invalidateAgent]);

  const handleBack = useCallback(() => {
    void navigate({ to: '/agents' });
  }, [navigate]);

  // ── Loading state ──
  if (isAgentLoading || !agentResponse?.data) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: '50vh' }}>
        <Spin />
      </Flex>
    );
  }

  const backButton = (
    <Button
      icon={<ChevronLeft size={14} />}
      style={{ fontSize: 12, padding: '8px 10px' }}
      onClick={handleBack}
    >
      Agents
    </Button>
  );

  // ── Left panel: header ──
  const leftHeader = canViewAgentChat ? (
    <Flex align="center" gap={10} flex={1}>
      {backButton}
      <Input
        value={agentName}
        onChange={handleNameChange}
        onBlur={handleNameBlur}
        disabled={!canEditAgent}
        variant="borderless"
        style={{ fontSize: 16, fontWeight: 600, flex: 1, minWidth: 0 }}
      />
    </Flex>
  ) : undefined;

  // ── Left panel: chat ──
  const leftContent = canViewAgentChat ? (
    <Flex vertical style={{ flex: 1, minHeight: 0, padding: '12px 24px' }}>
      {agentData?.builtInAssistantThread ? (
        <AiAssistantThread
          aiAssistantThreadId={agentData.builtInAssistantThread.id}
          withComposer={canEditAgent}
        />
      ) : (
        <Flex vertical align="center" justify="center" style={{ flex: 1 }}>
          <Typography.Title level={4} style={{ fontSize: 16, lineHeight: '26px' }}>
            No chat available
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 16, lineHeight: '26px' }}>
            This agent does not have an associated chat thread.
          </Typography.Text>
        </Flex>
      )}
    </Flex>
  ) : undefined;

  const launchControls = (
    <Flex gap={8}>
      {agentData?.state === 'draft' && (
        <Button
          type="primary"
          icon={<ArrowRight size={15} />}
          style={{ fontSize: 12 }}
          loading={isLaunching}
          onClick={handleLaunch}
        >
          Launch Agent
        </Button>
      )}
      {agentData?.state === 'active' && (
        <Button
          color="blue"
          variant="filled"
          icon={<Pause size={15} />}
          style={{ fontSize: 12 }}
          loading={isLaunching}
          onClick={handlePause}
        >
          Pause Agent
        </Button>
      )}
      {agentData?.state === 'paused' && (
        <Button
          color="green"
          variant="filled"
          icon={<PlayCircle size={15} />}
          style={{ fontSize: 12 }}
          loading={isLaunching}
          onClick={handleLaunch}
        >
          Resume Agent
        </Button>
      )}
    </Flex>
  );

  // ── Right panel: header ──
  const rightHeader = (
    <Flex align="center" justify="space-between" style={{ width: '100%' }}>
      <Flex align="center" gap={8} style={{ minWidth: 0, flex: 1 }}>
        {!canViewAgentChat && backButton}
        {!canViewAgentChat && (
          <Typography.Text
            strong
            ellipsis
            className="agent-layout-right-title"
            style={{ maxWidth: 240 }}
          >
            {agentName}
          </Typography.Text>
        )}
        <AdaptiveSegmented value={currentTab} onChange={handleTabChange} options={tabOptions} />
      </Flex>
      {launchControls}
    </Flex>
  );

  return (
    <AgentLayout
      leftHeader={leftHeader}
      left={leftContent}
      rightHeader={rightHeader}
      right={<Outlet />}
      mobileHeaderLeft={backButton}
    />
  );
};

export const Route = createFileRoute('/_authenticated/_app/agents/$agentId')({
  component: AgentDetailLayout,
});
